import Slider from 'rc-slider';
import React, { useEffect, useRef, useState } from 'react';

import { numberSeparator } from '../../helpers/formats';
import type QueryFilter from '../../helpers/query-filter';
import type { CombinedFacet } from '../../utils/reselectors';
import SelectFilterRangeHistogram from './select-filter-range-histogram';

const getUnit = (facet: CombinedFacet, value: number | null) => {
  const { suffix, suffixSingular } = facet;

  if (!suffixSingular) {
    return suffix;
  }

  const isPlural = (value || 0) > 1;
  return isPlural ? suffix : suffixSingular;
};

const formatNumber = (shouldFormat: boolean | undefined, value: number) => {
  if (shouldFormat) {
    return numberSeparator(value) || 0;
  }
  return value;
};

const getActualMaxValue = (facet: CombinedFacet, max: number | undefined) => {
  switch (facet.id) {
    case 'price':
      return 10000000;

    case 'mileage':
      return 100000;

    case 'enginePower':
      return 2000;

    default:
      return max;
  }
};

const createNewQueryFilter = (
  id: string,
  queryFilter: QueryFilter,
  from: number | null,
  to: number | null,
  min: number | undefined,
  max: number | undefined,
) => {
  const isFromMin = from === min;
  const isToMax = to === max;

  const fromValue = isFromMin ? null : from;
  const toValue = isToMax ? null : to;
  return queryFilter.removePagination().removeRange(id).addRange(id, fromValue, toValue);
};

interface SelectFilterRangeProps {
  facet: CombinedFacet;
  queryFilter: QueryFilter;
  onRequestSearch: (queryFilter: QueryFilter) => void;
  onCloseFoldout: () => void;
  onReset?: () => void;
}

const SelectFilterRange = ({
  facet,
  queryFilter,
  onCloseFoldout,
  onRequestSearch,
}: SelectFilterRangeProps) => {
  const { prefixChar: _prefixChar, suffixChar, rangeStep, valueFormat, range } = facet;
  const debounceRef = useRef<NodeJS.Timeout>(undefined);

  const min = facet.min;
  const sliderMax = facet.max;
  const actualMax = getActualMaxValue(facet, sliderMax);

  const [fromValue, setFromValue] = useState<number | null>(range?.min || 0);
  const [toValue, setToValue] = useState<number | null>(range?.max || 0);
  const [isFromFocused, setIsFromFocused] = useState(false);
  const [isToFocused, setIsToFocused] = useState(false);

  const fromValueRef = useRef(fromValue);
  fromValueRef.current = fromValue;
  const toValueRef = useRef(toValue);
  toValueRef.current = toValue;

  const interactionRef = useRef<'input' | 'slider'>('slider');

  const prefixChar = _prefixChar || ((fromValue ?? 0) <= (min ?? 0) ? '<' : null);

  const performSearch = () => {
    const hasNoChanges =
      (range?.min || 0) === fromValueRef.current && (range?.max || 0) === toValueRef.current;

    if (hasNoChanges) {
      return;
    }
    const newQueryFilter = createNewQueryFilter(
      facet.id,
      queryFilter,
      fromValueRef.current,
      toValueRef.current,
      interactionRef.current === 'slider' ? min : undefined,
      interactionRef.current === 'slider' ? sliderMax : undefined,
    );
    onRequestSearch(newQueryFilter);
  };

  const handleInputValueChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setValue: (value: number | null) => void,
  ) => {
    interactionRef.current = 'input';
    const value = e.currentTarget.value;
    const isEmpty = value === '';

    if (isEmpty) {
      return setValue(null);
    }

    const numberValue = Number.parseInt(value, 10);

    if (Number.isNaN(numberValue)) {
      setValue(null);
    } else {
      setValue(numberValue);
    }
  };

  const handleInputKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const isEnterClick = e.keyCode === 13;

    if (isEnterClick) {
      onCloseFoldout();
    }
  };

  const handleInputBlur = (setFocus: (focused: boolean) => void) => {
    setFocus(false);
  };

  const handleSliderValueChange = (value: number | number[]) => {
    interactionRef.current = 'slider';
    const isEditingInput = isFromFocused || isToFocused;

    if (isEditingInput) {
      return;
    }

    const [from, to] = value as number[];

    const hasFromChanged = from !== fromValue;
    const hasToChanged = to !== toValue;

    if (hasFromChanged) {
      setFromValue(from);
    } else if (hasToChanged) {
      setToValue(to);
    }
  };

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      performSearch();
    }, 250);
  }, [fromValue, toValue]);

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const fromUnit = getUnit(facet, fromValue);
  const toUnit = getUnit(facet, toValue);
  const minUnit = getUnit(facet, min || 0);
  const maxUnit = getUnit(facet, sliderMax || 0);

  const shouldFormatFrom = valueFormat && !isFromFocused;
  const shouldFormatTo = valueFormat && !isToFocused;

  const formattedFrom = fromValue === null ? '' : formatNumber(shouldFormatFrom, fromValue);
  const formattedTo = toValue === null ? '' : formatNumber(shouldFormatTo, toValue);

  const formattedMin = formatNumber(valueFormat, min || 0);
  const formattedSliderMax = formatNumber(valueFormat, sliderMax || 0);

  const sliderValueMin = ((fromValue ?? 0) < (min ?? 0) ? min : fromValue) ?? 0;
  const sliderValueMax = ((toValue ?? 0) > (actualMax ?? 0) ? actualMax : toValue) ?? 0;
  const sliderValue = [sliderValueMin, sliderValueMax];

  return (
    <>
      <div className="filter-panel-body-content">
        <div className="filter-panel-body-content-wrapper">
          <div data-wx-rangeslider>
            <div className="range-input-section">
              <div className="range-input-container">
                <div data-wx-inputtext="unit">
                  <input
                    type="text"
                    className="range-input"
                    name="min"
                    value={formattedFrom}
                    onChange={(e) => handleInputValueChange(e, setFromValue)}
                    onFocus={() => setIsFromFocused(true)}
                    onBlur={() => handleInputBlur(setIsFromFocused)}
                    onKeyUp={handleInputKeyUp}
                  />
                  <div className="input-text-unit">{fromUnit}</div>
                  <div className="input-text-ghost" />
                </div>
              </div>
              <div className="range-input-separator" />
              <div className="range-input-container">
                <div data-wx-inputtext="unit">
                  <input
                    type="text"
                    className="range-input"
                    name="max"
                    value={formattedTo}
                    onChange={(e) => handleInputValueChange(e, setToValue)}
                    onFocus={() => setIsToFocused(true)}
                    onBlur={() => handleInputBlur(setIsToFocused)}
                    onKeyUp={handleInputKeyUp}
                  />
                  <div className="input-text-unit">{toUnit}</div>
                  <div className="input-text-ghost" />
                </div>
              </div>
            </div>

            <div className="range-slider">
              <SelectFilterRangeHistogram facet={facet} />

              <Slider
                range={true}
                min={min}
                max={sliderMax}
                step={rangeStep}
                value={sliderValue}
                onChange={handleSliderValueChange}
              />
            </div>

            <div className="range-span">
              <div className="range-span-low">
                {prefixChar}
                {formattedMin} {minUnit}
              </div>
              <div className="range-span-high">
                {formattedSliderMax}
                {suffixChar} {maxUnit}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SelectFilterRange;
